// Read-only configuration previews for the pre-deployed modules in
// CF_MODULE_WHITELIST. When an admin picks a module from the deploy wizard's
// dropdown, the wizard renders the params below so they understand what the
// module enforces in practice — per Manny's April-21 feedback that admins
// "want to make an informed choice" when selecting a module.

export interface ModuleConfigField {
  label: string;
  value: string;
  hint?: string;
}

export interface ModuleConfig {
  id: string;
  fields: ModuleConfigField[];
}

export const MODULE_CONFIGS: Record<string, ModuleConfig> = {
  // ─── Auction modules ────────────────────────────────────────────────────────
  'mod-1': {
    id: 'mod-1', // Dutch Auction v2
    fields: [
      { label: 'Auction style', value: 'Descending price', hint: 'Price drops over time until a bidder accepts' },
      { label: 'Decay window', value: '6 hours' },
      { label: 'Min reserve', value: '60% of last oracle price' },
      { label: 'Settlement', value: 'Automatic on first valid bid' },
    ],
  },
  'mod-2': {
    id: 'mod-2', // Batch Clearing
    fields: [
      { label: 'Auction style', value: 'Batch / uniform clearing price' },
      { label: 'Bidding window', value: '4 hours' },
      { label: 'Clearing', value: 'Single uniform price across all winning bids' },
      { label: 'Min participation', value: '3 bidders' },
    ],
  },
  'mod-3': {
    id: 'mod-3', // Sealed Bid
    fields: [
      { label: 'Auction style', value: 'Sealed-bid with reveal phase' },
      { label: 'Bid phase', value: '24h commit + 12h reveal' },
      { label: 'Pricing', value: 'Second-price (Vickrey)' },
      { label: 'Min increment', value: '0.5%' },
    ],
  },

  // ─── Fee modules ────────────────────────────────────────────────────────────
  'mod-4': {
    id: 'mod-4', // Tiered Fee v2
    fields: [
      { label: 'Management fee', value: '0.50% APR', hint: 'Charged on deployed capital' },
      { label: 'Performance fee', value: '10% of yield > hurdle' },
      { label: 'Origination (< $100k)', value: '0.75%' },
      { label: 'Origination ($100k–$1M)', value: '0.50%' },
      { label: 'Origination (> $1M)', value: '0.25%' },
    ],
  },
  'mod-5': {
    id: 'mod-5', // Flat Fee v1
    fields: [
      { label: 'Origination fee', value: '0.50%', hint: 'Charged on borrow' },
      { label: 'Servicing fee', value: '0.25% APR', hint: 'Accrues on outstanding balance' },
      { label: 'Liquidation fee', value: '5.00%', hint: 'Charged on liquidation amount' },
    ],
  },
  'mod-6': {
    id: 'mod-6', // RWA Split Fee
    fields: [
      { label: 'Originator share', value: '60% of fee revenue' },
      { label: 'Manager share', value: '30%' },
      { label: 'Protocol share', value: '10%' },
      { label: 'Servicing fee', value: '0.40% APR' },
    ],
  },

  // ─── Risk modules ───────────────────────────────────────────────────────────
  'mod-7': {
    id: 'mod-7', // Standard Risk v2
    fields: [
      { label: 'Max LTV', value: '75%', hint: 'Loan-to-value ceiling at origination' },
      { label: 'Liquidation threshold', value: '85%' },
      { label: 'Haircut', value: '10%' },
      { label: 'Health-factor floor', value: '1.10' },
    ],
  },
  'mod-8': {
    id: 'mod-8', // Aggressive Growth
    fields: [
      { label: 'Max LTV', value: '85%' },
      { label: 'Liquidation threshold', value: '90%' },
      { label: 'Haircut', value: '5%' },
      { label: 'Health-factor floor', value: '1.05' },
    ],
  },
  'mod-9': {
    id: 'mod-9', // Institutional Guardrail
    fields: [
      { label: 'Max LTV', value: '60%' },
      { label: 'Liquidation threshold', value: '80%' },
      { label: 'Haircut', value: '15%' },
      { label: 'Health-factor floor', value: '1.20' },
      { label: 'Concentration cap', value: '20% per collateral' },
    ],
  },

  // ─── Oracle modules ─────────────────────────────────────────────────────────
  'mod-10': {
    id: 'mod-10', // Chainlink Core
    fields: [
      { label: 'Provider', value: 'Chainlink' },
      { label: 'Update model', value: 'Push (heartbeat + deviation)' },
      { label: 'Staleness threshold', value: '24h' },
      { label: 'Aggregation', value: 'Median of feed nodes' },
    ],
  },
  'mod-11': {
    id: 'mod-11', // Pyth Fast Oracle
    fields: [
      { label: 'Provider', value: 'Pyth Network' },
      { label: 'Update model', value: 'Pull (on-demand)' },
      { label: 'Confidence interval', value: 'Surfaced per quote' },
      { label: 'Cross-chain', value: 'Wormhole-attested' },
    ],
  },
  'mod-12': {
    id: 'mod-12', // RedStone Hybrid
    fields: [
      { label: 'Provider', value: 'RedStone' },
      { label: 'Update model', value: 'Hybrid (push + on-demand)' },
      { label: 'Staleness threshold', value: '12h push, ad-hoc on-demand' },
      { label: 'Modular feeds', value: 'Custom per-asset configurable' },
    ],
  },

  // ─── Valuation modules ──────────────────────────────────────────────────────
  'mod-13': {
    id: 'mod-13', // NAV Calculator v1
    fields: [
      { label: 'Methodology', value: 'NAV per share = (assets − liabilities) / shares' },
      { label: 'Update frequency', value: 'Daily at 23:00 UTC' },
      { label: 'Source', value: 'Fund administrator attestation' },
      { label: 'Validation', value: 'Auditor sign-off + onchain claim' },
    ],
  },

  // ─── Liquidity modules ──────────────────────────────────────────────────────
  'mod-14': {
    id: 'mod-14', // AMM Pool Router
    fields: [
      { label: 'Pool type', value: 'Constant-product AMM' },
      { label: 'Withdrawal model', value: 'On-demand with reserve buffer' },
      { label: 'Min reserve ratio', value: '15%' },
      { label: 'Max single-tx withdrawal', value: '$5M' },
    ],
  },

  // ─── Compliance modules ─────────────────────────────────────────────────────
  // Each Compliance Module is a single attachment to the CF (per ERC-3643 /
  // SEP-0057: one IModularCompliance per token, with bundled rule modules
  // bound internally). Fields below show the rule set the module enforces;
  // admins pick a module rather than composing individual rules.
  'mod-15': {
    id: 'mod-15', // Standard Institutional Compliance
    fields: [
      { label: 'Max transactions', value: '5 / 7 days', hint: 'Per actor rolling window' },
      { label: 'Borrow size', value: '$10,000 – $5,000,000' },
      { label: 'Jurisdiction TVL cap', value: 'US ≤ 40% of TVL' },
      { label: 'Trading window', value: 'Mon–Fri · 12:00–22:00 UTC' },
      { label: 'Min credit score', value: '650' },
    ],
  },
  'mod-16': {
    id: 'mod-16', // Aggressive RWA Compliance
    fields: [
      { label: 'Max transactions', value: '15 / 7 days' },
      { label: 'Borrow size', value: '$25,000 – $25,000,000' },
      { label: 'Jurisdiction TVL cap', value: 'No cap' },
      { label: 'Trading window', value: 'All days · 00:00–24:00 UTC' },
      { label: 'Min credit score', value: '600' },
    ],
  },
  'mod-17': {
    id: 'mod-17', // EU Qualified Compliance
    fields: [
      { label: 'Max transactions', value: '8 / 7 days' },
      { label: 'Borrow size', value: '€100,000 – €10,000,000', hint: 'MiFID II Qualified-Investor minimum' },
      { label: 'Jurisdiction filter', value: 'EU only', hint: 'Hard-block non-EU jurisdictions' },
      { label: 'Trading window', value: 'Mon–Fri · 08:00–18:00 UTC' },
      { label: 'Min credit score', value: '700' },
    ],
  },
  'mod-18': {
    id: 'mod-18', // Conservative Treasury Compliance
    fields: [
      { label: 'Max transactions', value: '2 / 7 days' },
      { label: 'Borrow size', value: '$1,000,000 – $50,000,000' },
      { label: 'Jurisdiction TVL cap', value: 'US ≤ 25% of TVL' },
      { label: 'Trading window', value: 'Mon–Fri · 14:00–20:00 UTC' },
      { label: 'Min credit score', value: '750' },
      { label: 'Cross-facility cap', value: '$20M per actor' },
    ],
  },
};

export function getModuleConfig(id: string): ModuleConfig | undefined {
  return MODULE_CONFIGS[id];
}
